import client from "services/ngage/api-client";
import {Trail, TrailAPIData, TrailUpdateAPIData} from "types/Trail";
import deserializeTrail from "services/ngage/deserializers/trail";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

export type TrailFilters = {
    search?: string | null
    page?: number
    perPage?: number
}

/**
 * Get the number of trails that have been created
 *
 * @param filters
 */
const count = async (filters: TrailFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 1}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<Trail>>('/trails', {params})).data

        return page?.meta.total ?? 0
    } catch (error) {
        console.error("Unable to fetch trails", error)
        return 0
    }
}

/**
 * Get all trail s that match the given filters (paginated)
 */
const filtered = async (filters: TrailFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    const page = (await client.get<DataResource<Trail>>('/trails', {params})).data

    if (page) page.data = page.data.map(deserializeTrail)

    return page
}

/**
 * Get a single trail 
 *
 * @param {number} trailId
 */
const get = async (trailId: number) => {
    try {
        const trail = (await client.get<Trail | null>(`/trails/${trailId}`)).data
        return trail ? deserializeTrail(trail) : null
    } catch (error) {
        console.error(`Unable to fetch trail ${trailId}`, error)
        return null
    }
}

/**
 * Store a new trail 
 *
 * @param data
 */
const store = async (data: TrailAPIData) => {
    try {
        const trail = (await client.post<Trail | null>('/trails', data)).data
        return trail ? deserializeTrail(trail) : null
    } catch (error) {
        console.error("Unable to store trail", error)
        throw error
    }
}

/**
 * Update an existing trail 
 *
 * @param {number} trailId
 * @param data
 */
const update = async (trailId: number, data: Partial<TrailUpdateAPIData>) => {
    try {
        const trail = (await client.put<Trail | null>(`/trails/${trailId}`, data)).data
        return trail ? deserializeTrail(trail) : null
    } catch (error) {
        console.error(`Unable to update trail ${trailId}`, error)
        throw error
    }
}

/**
 * Permanently delete a trail 
 *
 * @param {number} trailId
 */
const destroy = async (trailId: number) => {
    try {
        return (await client.delete<null>(`/trails/${trailId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete trail ${trailId}`, error)
        throw error
    }
}

const trails = {
    count,
    filtered,
    get,
    store,
    update,
    destroy
}

export default trails;